import {
  type FormEvent,
  type PropsWithChildren,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  AlertTriangle,
  LoaderCircle,
  LogIn,
  LogOut,
  RefreshCw,
  ShieldX,
  User,
  Lock,
  Eye,
  EyeOff,
} from 'lucide-react';
import { resolveLoginEmail } from './loginIdentity';
import { AnnouncementGate } from './AnnouncementGate';
import { ForqLoader } from './ForqLoader';
import { AccessRequestView, PasswordResetView } from './LoginExtras';
import { AccessStage } from '../access/AccessStage';
import { AccessTransition } from '../access/AccessTransition';
import { activeDemoAnnouncement } from '@forq/admin';
import {
  bootstrapApplication,
  loadSessionApplication,
  type ApplicationBootstrapResult,
} from '@forq/application';
import type { AuthGateway, AuthenticatedSession, Credentials } from '@forq/contracts';
import { createDemoServiceBillingGateway } from '@forq/service-billing-data';
import {
  foundationPermissions,
  foundationTenants,
  PlatformProvider,
} from '@forq/platform';
import {
  createSupabaseRuntimeServices,
  resolveSupabaseBootstrapConfiguration,
  type SupabaseRuntimeServices,
} from './runtime';
import {
  createPreviewPersistenceServices,
  resolvePreviewPersistenceConfiguration,
  type PreviewPersistenceServices,
} from './previewPersistence';
import { PreviewPersistenceProvider } from './PreviewPersistenceContext';

const preferredTenantStorageKey = 'forq.active-tenant';
const demoBillingGateway = createDemoServiceBillingGateway();

type RemoteBootstrapState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'error'; readonly message: string }
  | ApplicationBootstrapResult;

type PreviewBootstrapState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'anonymous' }
  | { readonly kind: 'ready'; readonly session: AuthenticatedSession }
  | { readonly kind: 'error'; readonly message: string };

function messageFrom(error: unknown): string {
  return error instanceof Error && error.message
    ? error.message
    : 'FORQ konnte nicht gestartet werden.';
}

function preferredTenantId(): string | null {
  try {
    return window.localStorage.getItem(preferredTenantStorageKey);
  } catch {
    return null;
  }
}

function rememberTenant(tenantId: string): void {
  try {
    window.localStorage.setItem(preferredTenantStorageKey, tenantId);
  } catch {
    // Browser storage is optional and never blocks FORQ.
  }
}

interface BoundaryFrameProps extends PropsWithChildren {
  readonly eyebrow: string;
  readonly title: string;
  readonly description: string;
  /** Fehlt das Symbol, traegt der Inhalt selbst das Bild -- etwa der Loader beim Start. */
  readonly icon?: ReactNode;
}

function BoundaryFrame({
  eyebrow,
  title,
  description,
  icon,
  children,
}: BoundaryFrameProps) {
  return (
    <main className="forq-bootstrap">
      <section className="forq-bootstrap__panel">
        <img className="forq-bootstrap__brand" src="/forq-wordmark.svg" alt="FORQ" width={1340} height={384} />
        {icon ? <div className="forq-bootstrap__icon">{icon}</div> : null}
        <span className="forq-eyebrow">{eyebrow}</span>
        <h1>{title}</h1>
        <p>{description}</p>
        {children}
      </section>
      <footer>FORQ · modulare ERP-Plattform</footer>
    </main>
  );
}

type LoginMode = 'signin' | 'reset' | 'access';

interface LoginViewProps {
  readonly busy: boolean;
  readonly error: string | null;
  readonly onSubmit: (credentials: Credentials) => Promise<void>;
  readonly preview?: boolean;
  /** Fehlt im Preview-Modus: dort gibt es keine Selbstbedienung. */
  readonly auth?: AuthGateway;
}

function LoginView({ busy, error, onSubmit, preview = false, auth }: LoginViewProps) {
  const [mode, setMode] = useState<LoginMode>('signin');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    // Supabase meldet technisch per E-Mail an -- ein reiner Benutzername wird auf die Firmen-E-Mail
    // abgebildet, eine vollständige E-Mail bleibt unverändert (siehe resolveLoginEmail).
    void onSubmit({ email: resolveLoginEmail(username), password });
  }

  const selfService = !preview && auth;

  if (selfService && mode === 'reset') {
    return (
      <AccessStage
        eyebrow="Passwort vergessen"
        title="Neues Passwort setzen"
        description="Gib deine E-Mail-Adresse an. Wir schicken dir einen Link, mit dem du ein neues Passwort vergeben kannst."
      >
        <PasswordResetView
          onBack={() => setMode('signin')}
          onSubmit={(address) => auth.requestPasswordReset(address, `${window.location.origin}/passwort-neu`)}
        />
      </AccessStage>
    );
  }

  if (selfService && mode === 'access') {
    return (
      <AccessStage
        eyebrow="Noch kein Konto"
        title="Zugang anfragen"
        description="Deine Anfrage landet in der FORQ-Administration. Sobald sie freigegeben ist, bekommst du eine Einladung per E-Mail."
      >
        <AccessRequestView onBack={() => setMode('signin')} onSubmit={(input) => auth.requestAccess(input)} />
      </AccessStage>
    );
  }

  return (
    <AccessStage
      eyebrow={preview ? 'FORQ Preview' : 'FORQ Access'}
      title={preview ? 'Preview-Datenbank anmelden' : 'Anmelden'}
      description={preview
        ? 'Melde dich mit einem Benutzerkonto des bestehenden Supabase-Projekts an. FORQ verwendet ausschließlich das getrennte Schema forq_next_preview.'
        : undefined}
    >
      <form className="forq-login-form" onSubmit={submit}>
        <label>
          {preview ? 'E-Mail-Adresse' : 'Benutzername'}
          <div className="forq-input">
            <User className="forq-input__icon" size={17} aria-hidden="true" />
            <input
              type="text"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              autoComplete="username"
              placeholder={preview ? 'name@firma.de' : 'z. B. f.befeld'}
              required
              autoFocus
            />
          </div>
        </label>
        <label>
          Passwort
          <div className="forq-input">
            <Lock className="forq-input__icon" size={17} aria-hidden="true" />
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              required
            />
            <button
              type="button"
              className="forq-input__reveal"
              onClick={() => setShowPassword((visible) => !visible)}
              aria-label={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
              tabIndex={-1}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </label>
        {error ? <div className="forq-bootstrap__error" role="alert">{error}</div> : null}
        <button className="forq-bootstrap__primary" type="submit" disabled={busy}>
          {busy ? <LoaderCircle className="forq-spin" size={18} /> : <LogIn size={18} />}
          {busy ? 'Anmeldung läuft …' : 'Anmelden'}
        </button>
        {selfService && (
          <div className="forq-login-foot">
            <button type="button" className="forq-login-link" onClick={() => setMode('reset')}>
              Passwort vergessen?
            </button>
            <button type="button" className="forq-login-link" onClick={() => setMode('access')}>
              Zugang anfragen
            </button>
          </div>
        )}
      </form>
    </AccessStage>
  );
}

interface SupabaseBootstrapProps extends PropsWithChildren {
  readonly services: SupabaseRuntimeServices;
}

function SupabaseBootstrap({ children, services }: SupabaseBootstrapProps) {
  const [state, setState] = useState<RemoteBootstrapState>({ kind: 'loading' });
  const [loginBusy, setLoginBusy] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  /**
   * Der Uebergang FORQ Access laeuft nach einer ECHTEN Anmeldung -- nicht beim Wiederherstellen
   * einer bestehenden Sitzung. Wer die Seite neu laedt, will an die Arbeit, nicht begruesst werden.
   */
  const [accessOpening, setAccessOpening] = useState(false);
  /** Wer gerade geladen ist -- Grundlage dafür, ein überflüssiges Neuladen zu erkennen. */
  const loadedUserId = useRef<string | null>(null);

  const refresh = useCallback(async (
    knownSession?: AuthenticatedSession | null,
  ): Promise<void> => {
    setState({ kind: 'loading' });
    try {
      const result = knownSession === undefined
        ? await bootstrapApplication(services, preferredTenantId())
        : knownSession === null
          ? { kind: 'anonymous' } as const
          : await loadSessionApplication(knownSession, services.platformData, preferredTenantId());
      loadedUserId.current = result.kind === 'anonymous' ? null : result.session.userId;
      setState(result);
    } catch (error: unknown) {
      loadedUserId.current = null;
      setState({ kind: 'error', message: messageFrom(error) });
    }
  }, [services]);

  useEffect(() => {
    void refresh();
    return services.auth.onSessionChange((session) => {
      // Zweite Sicherung neben dem Filter im Auth-Adapter: Ist es DIESELBE Person wie eben, gibt
      // es nichts neu zu laden. Ein Neuladen hier ist teuer -- es spielt die Startsequenz erneut
      // ab und wirft weg, woran gerade jemand arbeitet (eine halb zugeordnete Importdatei etwa).
      if (session && session.userId === loadedUserId.current) return;
      void refresh(session);
    });
  }, [refresh, services.auth]);

  const signIn = useCallback(async (credentials: Credentials) => {
    setLoginBusy(true);
    setLoginError(null);
    try {
      const session = await services.auth.signIn(credentials);
      // VOR dem Laden, nicht danach: sonst liegt zwischen Anmeldung und Willkommen der alte
      // Zwischenbildschirm. Der Vorbereitungszustand von FORQ Access IST das Warten -- er bleibt
      // stehen, solange Sitzung, Mandant, Rechte und Arbeitsplatz geladen werden.
      setAccessOpening(true);
      await refresh(session);
    } catch (error: unknown) {
      setAccessOpening(false);
      setState({ kind: 'anonymous' });
      setLoginError(messageFrom(error));
    } finally {
      setLoginBusy(false);
    }
  }, [refresh, services.auth]);

  const signOut = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      await services.auth.signOut();
      setState({ kind: 'anonymous' });
    } catch (error: unknown) {
      setState({ kind: 'error', message: messageFrom(error) });
    }
  }, [services.auth]);

  // Nach einer Anmeldung haelt FORQ Access den Bildschirm -- auch waehrend noch geladen wird.
  // Der alte Zwischenbildschirm bleibt fuer den Fall, dass ohne Anmeldung geladen wird (Neuladen
  // mit bestehender Sitzung); dort gibt es nichts zu begruessen.
  if (state.kind === 'loading' && !accessOpening) {
    return (
      <BoundaryFrame eyebrow="Systemstart" title="FORQ wird vorbereitet" description="Sitzung, Firmenzuordnung und Berechtigungen werden sicher geladen.">
        <ForqLoader className="forq-bootstrap__loader" size={96} label={null} />
      </BoundaryFrame>
    );
  }
  if (state.kind === 'loading') {
    // Die Flaeche bleibt DIESELBE, wenn gleich `ready` kommt -- gleicher Typ an gleicher Stelle,
    // also behaelt React sie und das Willkommen faengt nicht von vorn an.
    return <AccessTransition ready={false} onFinished={() => setAccessOpening(false)} />;
  }
  if (state.kind === 'anonymous') {
    return <LoginView busy={loginBusy} error={loginError} onSubmit={signIn} auth={services.auth} />;
  }
  if (state.kind === 'no-tenant') {
    return (
      <BoundaryFrame eyebrow="Zugriff vollständig geprüft" title="Noch keiner Firma zugeordnet" description={`${state.user.displayName}, dein Benutzerkonto ist aktiv, besitzt aber noch keine Mandantenzuordnung. Ein Administrator kann diese im FORQ Admincenter ergänzen.`} icon={<ShieldX size={28} />}>
        <button className="forq-bootstrap__secondary" type="button" onClick={() => void signOut()}><LogOut size={18} /> Abmelden</button>
      </BoundaryFrame>
    );
  }
  if (state.kind === 'error') {
    return (
      <BoundaryFrame eyebrow="Start nicht möglich" title="FORQ konnte nicht geladen werden" description={state.message} icon={<AlertTriangle size={28} />}>
        <div className="forq-bootstrap__actions">
          <button className="forq-bootstrap__primary" type="button" onClick={() => void refresh()}><RefreshCw size={18} /> Erneut versuchen</button>
          <button className="forq-bootstrap__secondary" type="button" onClick={() => void signOut()}><LogOut size={18} /> Abmelden</button>
        </div>
      </BoundaryFrame>
    );
  }

  return (
    <PlatformProvider
      key={`supabase:${state.session.userId}`}
      initialState={{ mode: 'supabase', tenants: state.tenants, activeTenantId: state.activeTenantId, user: state.user, permissions: state.permissions }}
      resolvePermissions={(tenantId) => services.platformData.resolvePermissions(state.session.userId, tenantId)}
      onActiveTenantChange={rememberTenant}
      onSignOut={signOut}
    >
      {children}
      {/* Nach dem Uebergang, nicht davor: die Meldung gehoert vor die Arbeit, nicht unter den
          Willkommensschleier -- sonst liest sie jemand, waehrend davor noch etwas laeuft. */}
      {accessOpening ? null : <AnnouncementGate announcement={state.announcement} />}
      {accessOpening ? (
        <AccessTransition
          ready
          name={state.user.displayName.split(' ')[0]}
          onFinished={() => setAccessOpening(false)}
        />
      ) : null}
    </PlatformProvider>
  );
}

function PreviewSupabaseBootstrap({
  children,
  services,
}: PropsWithChildren<{ readonly services: PreviewPersistenceServices }>) {
  const [state, setState] = useState<PreviewBootstrapState>({ kind: 'loading' });
  const [loginBusy, setLoginBusy] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  const authorize = useCallback(async (session: AuthenticatedSession | null) => {
    if (!session) {
      setState({ kind: 'anonymous' });
      return;
    }
    setState({ kind: 'loading' });
    try {
      await services.ensureAccess();
      setState({ kind: 'ready', session });
    } catch (error: unknown) {
      setState({ kind: 'error', message: messageFrom(error) });
    }
  }, [services]);

  useEffect(() => {
    void services.auth.getSession().then(authorize).catch((error: unknown) => {
      setState({ kind: 'error', message: messageFrom(error) });
    });
    return services.auth.onSessionChange((session) => { void authorize(session); });
  }, [authorize, services.auth]);

  const signIn = useCallback(async (credentials: Credentials) => {
    setLoginBusy(true);
    setLoginError(null);
    try {
      await authorize(await services.auth.signIn(credentials));
    } catch (error: unknown) {
      setState({ kind: 'anonymous' });
      setLoginError(messageFrom(error));
    } finally {
      setLoginBusy(false);
    }
  }, [authorize, services.auth]);

  const signOut = useCallback(async () => {
    await services.auth.signOut();
    setState({ kind: 'anonymous' });
  }, [services.auth]);

  if (state.kind === 'loading') {
    return <BoundaryFrame eyebrow="Preview-Datenbank" title="Zugriff wird geprüft" description="FORQ prüft ausschließlich den getrennten Preview-Datenbereich." icon={<LoaderCircle className="forq-spin" size={28} />} />;
  }
  if (state.kind === 'anonymous') return <LoginView busy={loginBusy} error={loginError} onSubmit={signIn} preview />;
  if (state.kind === 'error') {
    return (
      <BoundaryFrame eyebrow="Preview-Zugriff nicht möglich" title="FORQ konnte die Preview-Datenbank nicht öffnen" description={state.message} icon={<AlertTriangle size={28} />}>
        <div className="forq-bootstrap__actions">
          <button className="forq-bootstrap__primary" type="button" onClick={() => void services.auth.getSession().then(authorize)}><RefreshCw size={18} /> Erneut prüfen</button>
          <button className="forq-bootstrap__secondary" type="button" onClick={() => void signOut()}><LogOut size={18} /> Abmelden</button>
        </div>
      </BoundaryFrame>
    );
  }

  const activeTenantId = foundationTenants.some((tenant) => tenant.id === preferredTenantId())
    ? preferredTenantId() as string
    : foundationTenants[0].id;
  const user = {
    id: state.session.userId,
    displayName: state.session.displayName ?? state.session.email ?? 'Preview-Benutzer',
    role: 'FORQ Preview',
  };

  return (
    <PreviewPersistenceProvider value={{ mode: 'supabase-preview', billingGateway: services.billing }}>
      <PlatformProvider
        initialState={{ mode: 'demo', tenants: foundationTenants, activeTenantId, user, permissions: foundationPermissions }}
        onActiveTenantChange={rememberTenant}
        onSignOut={signOut}
      >
        {children}
      </PlatformProvider>
    </PreviewPersistenceProvider>
  );
}

export function AppBootstrap({ children }: PropsWithChildren) {
  const configuration = resolveSupabaseBootstrapConfiguration(import.meta.env);
  const previewConfiguration = resolvePreviewPersistenceConfiguration(import.meta.env);

  if (configuration.kind === 'configuration-error') {
    return <BoundaryFrame eyebrow="Konfigurationsfehler" title="FORQ ist nicht vollständig eingerichtet" description={configuration.message} icon={<AlertTriangle size={28} />} />;
  }

  if (configuration.kind === 'supabase') {
    // Der Vorschlags-Gateway wird jetzt IM Supabase-Zweig gesetzt (siehe ConfiguredSupabaseBootstrap),
    // damit er denselben angemeldeten Client benutzt. Vorher stand hier fest der Demo-Gateway --
    // jeder Rechnungsvorschlag war damit nach dem Neuladen verloren.
    return <ConfiguredSupabaseBootstrap config={configuration.config}>{children}</ConfiguredSupabaseBootstrap>;
  }

  if (previewConfiguration.kind === 'configuration-error') {
    return <BoundaryFrame eyebrow="Preview-Konfigurationsfehler" title="Die Preview-Datenbank ist nicht vollständig eingerichtet" description={previewConfiguration.message} icon={<AlertTriangle size={28} />} />;
  }

  if (previewConfiguration.kind === 'supabase-preview') {
    return <ConfiguredPreviewSupabaseBootstrap config={previewConfiguration.config}>{children}</ConfiguredPreviewSupabaseBootstrap>;
  }

  return (
    <PreviewPersistenceProvider value={{ mode: 'session-demo', billingGateway: demoBillingGateway }}>
      <PlatformProvider>
        {children}
        {/* Im Demo-Modus dieselbe Meldung, die das Demo-Adminpanel als aktiv fuehrt -- sonst
            behauptet die eine Flaeche etwas, das die andere nicht zeigt. */}
        <AnnouncementGate announcement={activeDemoAnnouncement} />
      </PlatformProvider>
    </PreviewPersistenceProvider>
  );
}

function ConfiguredSupabaseBootstrap({
  children,
  config,
}: PropsWithChildren<{ readonly config: { readonly url: string; readonly anonKey: string } }>) {
  const services = useMemo(() => createSupabaseRuntimeServices(config), [config.anonKey, config.url]);
  return (
    // Service-Rechnungsvorschlaege liegen im Produktivbetrieb in der Datenbank (public.service_billing_proposals).
    <PreviewPersistenceProvider value={{ mode: 'supabase', billingGateway: services.serviceBilling }}>
      <SupabaseBootstrap services={services}>{children}</SupabaseBootstrap>
    </PreviewPersistenceProvider>
  );
}

function ConfiguredPreviewSupabaseBootstrap({
  children,
  config,
}: PropsWithChildren<{ readonly config: { readonly url: string; readonly anonKey: string } }>) {
  const services = useMemo(() => createPreviewPersistenceServices(config), [config.anonKey, config.url]);
  return <PreviewSupabaseBootstrap services={services}>{children}</PreviewSupabaseBootstrap>;
}
